/**
 * ModuleCard — a card showing one module from the gallery.
 *
 * Reusable for: home page list, /modules index, /bundles/:id detail
 * (showing sub-modules), search results.
 */

import { Link } from 'react-router-dom';
import type { RegistryModule } from '../types/registry';
import './ModuleCard.css';

interface ModuleCardProps {
  module: RegistryModule;
  onSelect?: (id: string) => void;
  selected?: boolean;
  showActions?: boolean;
}

export function ModuleCard({
  module,
  onSelect,
  selected = false,
  showActions = true,
}: ModuleCardProps) {
  const facets = module.available_facets?.length ?? 0;
  const isChrome = module.tags.includes('chrome');
  const isEngine = module.tags.includes('engine') || module.tags.includes('ai');

  return (
    <article className={`module-card ${selected ? 'module-card--selected' : ''}`}>
      <header className="module-card__header">
        <h3 className="module-card__id">
          {onSelect ? (
            <button
              type="button"
              className="module-card__select"
              onClick={() => onSelect(module.id)}
            >
              {module.id}
            </button>
          ) : (
            <Link to={`/modules/${module.id}`}>{module.id}</Link>
          )}
        </h3>
        <span className={`module-card__tier module-card__tier--${module.tier}`}>
          {module.tier}
        </span>
      </header>

      <div className="module-card__meta">
        <span className="module-card__version">v{module.version}</span>
        <span className="module-card__category">{module.category}</span>
        {isChrome && <span className="module-card__chip">chrome</span>}
        {isEngine && <span className="module-card__chip">engine</span>}
        {facets > 1 && <span className="module-card__chip">{facets} facets</span>}
        {module.deprecated && (
          <span className="module-card__chip module-card__chip--warn">deprecated</span>
        )}
      </div>

      {module.subcategory && (
        <p className="module-card__subcategory">{module.subcategory}</p>
      )}

      <ul className="module-card__tags">
        {module.tags.slice(0, 4).map((t) => (
          <li key={t} className="module-card__tag">{t}</li>
        ))}
        {module.tags.length > 4 && (
          <li className="module-card__tag module-card__tag--more">
            +{module.tags.length - 4}
          </li>
        )}
      </ul>

      {showActions && (
        <footer className="module-card__actions">
          <Link to={`/pact-viewer/${module.id}`} className="module-card__btn">
            View
          </Link>
          <button
            type="button"
            className="module-card__btn module-card__btn--ghost"
            onClick={() => alert('Install: API not yet available')}
          >
            Install
          </button>
          <button
            type="button"
            className="module-card__btn module-card__btn--ghost"
            onClick={() => alert('Fork: API not yet available')}
          >
            Fork
          </button>
        </footer>
      )}
    </article>
  );
}
